import { client } from "@/hono";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { cn, responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { Nullable, WikiPageTreeItemWithChildren } from "@msuite/utils/types";
import { useNavigate } from "react-router";
import { WikiPageTree } from "./wiki-page-tree";

/** Props Interface */
interface WikiPageTreeItemProps {
	spaceId: string;
	page: WikiPageTreeItemWithChildren;
	activePageId: Nullable<string>;
}

export function WikiPageTreeItem({ spaceId, page, activePageId }: WikiPageTreeItemProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const deletePageModal = useAsyncModal();
	const collapse = useDisclosure({ defaultIsOpen: true });

	/** Functions */
	async function handleOnAddSubPage() {
		const response = await client.wiki.spaces[":spaceId"].page.$post({
			param: { spaceId },
			json: { parentPageId: page.id },
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		if (data.page) navigate(`/wiki/${spaceId}/${data.page.id}?create=true`);
	}

	async function handleOnCopyPage() {
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].copy.$post({
			param: { spaceId, pageId: page.id },
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		if (data.page) navigate(`/wiki/${spaceId}/${data.page.id}`);
	}

	async function handleOnDeletePage() {
		const isConfirmed = await deletePageModal.confirm();
		if (!isConfirmed) return;
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].$delete({
			param: { spaceId, pageId: page.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		if (isActive) navigate(`/wiki/${spaceId}`);
	}

	/** Computed */
	const children = page._children ?? [];
	const hasChildren = children.length > 0;
	const isActive = page.id === activePageId;

	/** Render */
	return (
		// The wrapper holds the row and its children together, so the spine beside an item that
		// has a sibling below it runs past the whole expanded branch, not just the row.
		<div className="tree-branch">
			{/* The elbow hangs off this borderless wrapper, not off the card: an absolutely placed
			    pseudo-element measures from its parent's padding edge, and the card's half-pixel
			    border would shift the elbow half a pixel off the spine. */}
			<div className="tree-branch-row">
				<div
					className={cn(
						"group h-stack justify-between cursor-pointer",
						"card",
						// The open page is marked the way the app marks a chosen item elsewhere — the date
						// picker's day, the file explorer's row: a wash of the accent, nothing louder.
						isActive && "bg-accent-500/10 dark:bg-accent-500/20",
					)}
					onClick={() => navigate(`/wiki/${spaceId}/${page.id}`)}
					onKeyUp={() => navigate(`/wiki/${spaceId}/${page.id}`)}
				>
					<div className="h-stack min-w-0">
						{hasChildren ? (
							<Button
								size="xxs"
								variant="plain"
								icon={
									<Icon
										name={collapse.isOpen ? "ChevronDown" : "ChevronRight"}
										size={14}
									/>
								}
								onClick={(event) => {
									event.stopPropagation();
									collapse.onToggle();
								}}
							/>
						) : (
							<Icon
								name="FileText"
								size={14}
								className="shrink-0 opacity-50"
							/>
						)}
						<p className={cn("truncate text-sm", isActive && "font-medium")}>
							{guardString(page.title, t("wiki.untitled"))}
						</p>
					</div>
					<div
						className="opacity-0 group-hover:opacity-100"
						onClick={(event) => event.stopPropagation()}
						onKeyUp={(event) => event.stopPropagation()}
					>
						<Menu
							items={() => [
								{
									icon: icon("Plus"),
									label: t("wiki.add_page"),
									onClick: handleOnAddSubPage,
								},
								{
									icon: icon("Edit"),
									label: t("wiki.edit_page"),
									onClick: () => navigate(`/wiki/${spaceId}/${page.id}?edit=true`),
								},
								{
									icon: icon("Copy"),
									label: t("wiki.copy_page"),
									onClick: handleOnCopyPage,
								},
								"divider",
								{
									icon: icon("Trash2"),
									label: t("wiki.delete_page"),
									onClick: handleOnDeletePage,
									isDanger: true,
								},
							]}
						>
							<Button
								size="xxs"
								variant="ghost"
								icon={<Icon name="EllipsisVertical" />}
							/>
						</Menu>
					</div>
				</div>
			</div>
			{hasChildren && collapse.isOpen && (
				<WikiPageTree
					// The spine lands under the chevron's centre: the card's half-pixel border, its
					// padding (px-3), then half of the 14px icon.
					className="ml-[calc(0.75rem+7.5px)]"
					spaceId={spaceId}
					pages={children}
					activePageId={activePageId}
				/>
			)}
			<ConfirmModal
				controller={deletePageModal}
				title={t("wiki.delete_page")}
				message={
					hasChildren
						? t("wiki.are_you_sure_you_want_to_delete_this_page_and_all_its_content")
						: t("wiki.are_you_sure_you_want_to_delete_this_page")
				}
			/>
		</div>
	);
}
